import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from "react-hook-form";
import ApiClient from "../services/api-client"
import Select from 'react-select'
import { CustomConfirmAlert as confirmAlert } from '../components/custom-confirm-alert';
import { useNavigate } from 'react-router-dom';
import BranchesEditableGrid from '../components/aggrids/editable-aggrids/company-branches-grid';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import MyDataHelpModal from "../components/modals/mydata-help.component";
import MyDataSubscriptionModal from "../components/modals/mydata-subscription-modal";
import MyDataCheckAfmHelpModal from "../components/modals/mydata-checkafm-help.component";
import FormLayout from '../components/layout/form-layout'
import BlockUi from '@availity/block-ui';
import Tippy from '@tippyjs/react';
import useStore from "../hooks/useStore";
import Commons from '../util/commons'

const buildSchema = (t) => Yup.object().shape({
    name: Yup.string().required(t("fieldRequired"))
        .max(80, t("maxLengthValidation", { maxlength: 80 })),
    taxpayerId: Yup.string().required(t("fieldRequired"))
        .max(10, t("maxLengthValidation", { maxlength: 10 })),
    gemh: Yup.string().nullable().transform((curr, orig) => orig === '' ? null : curr)
        .min(11, t("minLengthValidation", { minlength: 11 }))
        .max(12, t("maxLengthValidation", { maxlength: 12 })),
});

export default function CompanyWithBranchesAdd() {

    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const stripeParam = searchParams.get('stripe');
    const { t } = useTranslation();
    const navigate = useNavigate();

    const setSelectedCompany = useStore(s => s.setSelectedCompany);

    const refMyDataHelp = useRef();
    const refMyDataCheckAfmHelp = useRef();
    const refMySubscriptionModal = useRef();
    const branchEditableGrid = useRef();
    const inputRefParochosPassword = useRef(null);
    const inputRefMellonApiKey = useRef(null);
    const inputRefCheckAfmPassword = useRef(null);

    const validationSchema = useMemo(() => buildSchema(t), [t]);

    const { register, getValues, control, handleSubmit, setValue, reset, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema)
    });

    const [blockuiLoading, setBlockuiLoading] = useState(false);
    const [taxOffices, setTaxOffices] = useState([]);
    const [posTerminalTypes, setPosTerminalTypes] = useState([]);
    const [posTerminals, setPosTerminals] = useState([]);

    const handleApiError = (error) => {
        toast.error(error.message + ": " + error.response?.data?.message);
        setBlockuiLoading(false);
    };

    const toggleFieldVisibility = (ref, visible) => {
        ref.current?.classList.toggle('passwordLike', !visible);
    };

    function saveCompany(company) {
        const branchData = branchEditableGrid.current.getData();

        if (branchEditableGrid.current.hasError() && branchData.length > 0) {
            toast.error(t("entriesErrors"), 5000);
            return;
        }
        if (branchData.length === 0) {
            toast.error(t("atLeastOneBrancheRequired"), 5000);
            return;
        }

        if (id !== undefined) company.id = id;

        if (company.taxOffice?.value) {
            company.taxOffice = { id: company.taxOffice.value };
        } else {
            delete company.taxOffice;
        }

        company.branches = branchData.map(item => ({
            id: item.id,
            name: item.name,
            address: item.address,
            branchNumber: item.branchNumber,
            email: item.email,
            phone: item.phone,
            subjectField: item.subjectField,
        }));

        if (company.myDataUserId) company.myDataUserId = company.myDataUserId.trim();
        if (company.myDataToken) company.myDataToken = company.myDataToken.trim();

        console.debug(company)

        confirmAlert({
            title: t("submitConfirm"),
            message: t("areYouSure"),
            buttons: [
                {
                    label: t('Yes'),
                    onClick: () => {
                        setBlockuiLoading(true);
                        ApiClient.saveCompany(company).then(
                            response => {
                                if (id === undefined) navigate("/usercompany");
                                const newCompany = response.data;
                                if (newCompany.id === useStore.getState().selectedCompany.id) {
                                    localStorage.setItem("company", JSON.stringify(newCompany));
                                    setSelectedCompany(newCompany);
                                    Commons.setDefault("branch", undefined);
                                    Commons.setDefault("invoiceType", undefined);
                                    Commons.setDefault("series", undefined);
                                }
                                toast.success(t("saveSucceded"));
                                document.body.scrollTo(0, 0);
                                setBlockuiLoading(false);
                            },
                            handleApiError
                        );
                    }
                },
                { label: t('No') }
            ]
        });
    }

    const onSubmit = (company, e) => {
        e.preventDefault();
        saveCompany(company);
    };

    const myDATASubscriptionCallback = (data) => {
        setValue("myDataUserId", data.username);
        setValue("myDataToken", data.subscriptionKey);
        if (data.username && data.subscriptionKey) {
            toast.warn(t("myDATAKeysFetchedSuccesfully"));
        }
    };

    const onDeleteClick = e => {
        e.preventDefault();
        confirmAlert({
            title: t('submitConfirm'),
            message: t('areYouSureToDelete'),
            buttons: [
                {
                    label: t('Yes'),
                    onClick: () => ApiClient.deleteCompany(id).then(
                        () => {
                            navigate("/usercompanies");
                            toast.success(t("deleteSucceded"));
                        },
                        handleApiError
                    )
                },
                { label: t('No') }
            ]
        });
    };

    useEffect(() => {
        const _selectedCompanyId = useStore.getState().selectedCompany.id;

        ApiClient.getCompanyView(_selectedCompanyId).then(response => {
            const _data = response.data;

            const mappedTaxOffices = _data.taxOffices.map(el => ({ value: el.id, label: el.name }));
            setTaxOffices(mappedTaxOffices);

            setPosTerminalTypes(_data.posTerminalTypes ?? []);

            if (id !== undefined) {
                ApiClient.getCompany(id).then(
                    response => {
                        const comp = response.data;

                        const companyBranches = comp.branches.map(el => ({
                            id: el.id,
                            name: el.name,
                            address: el.address,
                            branchNumber: el.branchNumber.toString(),
                            email: el.email,
                            subjectField: el.subjectField,
                            phone: el.phone,
                            logo: el.logo ? el.logo + "?" + Date.now() : process.env.PUBLIC_URL + '/default.png',
                            model: el,
                        }));

                        reset({
                            name: comp.name,
                            taxpayerId: comp.taxpayerId,
                            gemh: comp.gemh,
                            myDataUserId: comp.myDataUserId,
                            myDataToken: comp.myDataToken,
                            vivaQuickPay: comp.vivaQuickPay,
                            stripeAccountID: comp.stripeAccountID,
                            accountantEmail: comp.accountantEmail,
                            webhook: comp.webhook,
                            logoPath: comp.logoPath,
                            parochosSubscriptionKey: comp.parochosSubscriptionKey,
                            parochosEmail: comp.parochosEmail,
                            parochosPassword: comp.parochosPassword,
                            mellonApiKey: comp.mellonApiKey,
                            mellonAuthCode: comp.mellonAuthCode,
                            myDATAProvider: comp.myDATAProvider ?? "MYDATA",
                            checkAfmUsername: comp.checkAfmUsername,
                            checkAfmPassword: comp.checkAfmPassword,
                            posTerminalType: comp.posTerminalType,
                            taxOffice: comp.taxOffice ? { value: comp.taxOffice.id, label: comp.taxOffice.name } : null,
                        });

                        setPosTerminals(comp.posTerminals ?? []);
                        branchEditableGrid.current.setData(companyBranches);

                        if (stripeParam) {
                            localStorage.setItem("company", JSON.stringify(comp));
                        }
                    },
                    response => toast.error("Error: " + response.data?.message)
                );
            } else {
                reset();
            }
        });
    }, [id]);

    const testMyDataKeys = (e) => {
        e.preventDefault();
        const { myDataUserId, myDataToken, taxpayerId } = getValues();
        setBlockuiLoading(true);
        ApiClient.testMyDataKeys(id, myDataUserId, myDataToken, taxpayerId).then(
            response => {
                toast[response.data ? 'success' : 'error'](t(response.data ? "rightKeys" : "wrongKeys"));
                setBlockuiLoading(false);
            },
            handleApiError
        );
    };

    const createStripeAccount = (e) => {
        e.preventDefault();
        ApiClient.createStripeAccount(useStore.getState().selectedCompany.id).then(
            response => { window.location.href = response.data; },
            handleApiError
        );
    };

    const reedemAuthorizationCode = (e) => {
        e.preventDefault();
        const companyId = useStore.getState().selectedCompany?.id;
        ApiClient.reedemAuthorizationCode(companyId, getValues().mellonAuthCode).then(
            response => { setValue("mellonApiKey", response.data); },
            handleApiError
        );
    };

    const syncTerminalList = (e) => {
        e.preventDefault();
        const companyId = useStore.getState().selectedCompany?.id;
        ApiClient.syncTerminalList(companyId).then(
            response => { window.location.href = response.data; },
            handleApiError
        );
    };

    return (
        <BlockUi tag="div" blocking={blockuiLoading}>
            <div className="container-fluid" style={{ padding: 0 }}>
                <form id="company_form" onSubmit={handleSubmit(onSubmit)}>
                    <FormLayout
                        footer={
                            <div className="form-group text-center">
                                <button type="submit" className="btn btn-skin btn-lg">{t("Save")}</button>
                            </div>
                        }
                        title={id ? t('companyUpdate') : t('companyAdd')}
                    >

                        {/* Basic info */}
                        <div className="card mainInvoiceInfoCard mb-3">
                            <div className="card-body">
                                <div className="row g-2">
                                    <div className="col-md-3 form-group required">
                                        <label className="control-label inputlabel">{t('CompanyName')}</label>
                                        <input name="name" type="text" {...register('name', { required: true, maxLength: 64 })} className={`form-control ${errors.name ? 'is-invalid' : ''}`} />
                                        <div className="invalid-feedback">{errors.name?.message}</div>
                                    </div>
                                    <div className="col-md-3 form-group required">
                                        <label className="control-label inputlabel">{t('taxpayerId')}</label>
                                        <input name="taxpayerId" type="text" maxLength="10" disabled={id != null} {...register('taxpayerId', { required: true, maxLength: 10 })} className={`form-control ${errors.taxpayerId ? 'is-invalid' : ''}`} />
                                        <div className="invalid-feedback">{errors.taxpayerId?.message}</div>
                                    </div>
                                    <div className="col-md-3 selectInputContainer form-group">
                                        <label className="control-label inputlabel">{t("taxOffice")}</label>
                                        <Controller
                                            render={({ field }) => (
                                                <Select {...field} options={taxOffices} inputId="taxoffice-react-select" className={`form-control ${errors.taxOffices ? 'is-invalid' : ''}`} placeholder={t('Select')} />
                                            )}
                                            control={control}
                                            rules={{ required: false }}
                                            name="taxOffice"
                                            defaultValue=""
                                        />
                                        <div className="invalid-feedback">{errors.taxOffice?.message}</div>
                                    </div>
                                    <div className="col-md-3 form-group">
                                        <label className="control-label inputlabel">{t('gemh')}</label>
                                        <input name="gemh" type="text" {...register('gemh', { maxLength: 12, minLength: 11 })} className={`form-control ${errors.gemh ? 'is-invalid' : ''}`} />
                                        <div className="invalid-feedback">{errors.gemh?.message}</div>
                                    </div>
                                </div>
                                {id && <div className="row mt-2 justify-content-end"><p><small>{t("newCompanyChoice")}<span><a href="/usercompanies">{t("here")}</a></span></small></p></div>}
                            </div>
                        </div>

                        {/* Branches */}
                        <div className="card mainInvoiceInfoCard mb-3">
                            <div className="card-header">
                                <i className="fa fa-map-marker" style={{ marginRight: 8 }}></i>
                                {t('Branches')}
                            </div>
                            <div className="card-body">
                                <BranchesEditableGrid ref={branchEditableGrid} />
                            </div>
                        </div>

                        {/* myDATA */}
                        <div className="card mainInvoiceInfoCard mb-3">
                            <div className="card-header">
                                <i className="fa fa-database" style={{ marginRight: 8 }}></i>
                                myDATA
                            </div>
                            <div className="card-body">
                                <p className="text-muted" style={{ fontSize: 13 }}>{t("mydataInfo")}</p>
                                <div className="mb-3 text-center">
                                    <a style={{ margin: 6, display: 'none' }} className="btn btn-skin" href="#" onClick={() => refMySubscriptionModal.current.open()}>{t("createMyDataApiKeysAuto")}</a>
                                    <a style={{ margin: 6 }} className="btn btn-skin" href="#" onClick={() => refMyDataHelp.current.open()}>{t("createMyDataApiKeysNonAuto")}</a>
                                </div>
                                <div className="row g-2 align-items-end">
                                    <div className="col-md-2 form-group">
                                        <div className="form-check">
                                            <input name="myDATAProvider" type="radio" value="MYDATA" {...register('myDATAProvider')} className="form-check-input" id="radio1" />
                                            <label className="form-check-label inputlabel" htmlFor="radio1">{t("myDataUsage")}</label>
                                        </div>
                                    </div>
                                    <div className="col-md-3 form-group">
                                        <label className="control-label inputlabel">{t("mydataUserId")}</label>
                                        <input name="myDataUserId" type="text" {...register('myDataUserId')} className={`form-control ${errors.myDataUserId ? 'is-invalid' : ''}`} />
                                        <div className="invalid-feedback">{errors.myDataUserId?.message}</div>
                                    </div>
                                    <div className="col-md-4 form-group">
                                        <label className="control-label inputlabel">{t('mydataToken')}
                                            <Tippy trigger="click" content={t("mydataTokenInfo")}><i className="fa fa-x2 fa-info-circle infoicon"></i></Tippy>
                                        </label>
                                        <div className="input-group">
                                            <input name="myDataToken" type="text" {...register('myDataToken')} className={`form-control ${errors.myDataToken ? 'is-invalid' : ''}`} />
                                            <button className="btn btn-skin" onClick={testMyDataKeys}>{t("testKeysBtnLabel")}</button>
                                            <div className="invalid-feedback">{errors.myDataToken?.message}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Parochos */}
                        <div className="card mainInvoiceInfoCard mb-3">
                            <div className="card-header">
                                <i className="fa fa-cloud" style={{ marginRight: 8 }}></i>
                                {t('ParochosInfo')}
                            </div>
                            <div className="card-body">
                                <div className="row g-2 align-items-end">
                                    <div className="col-md-1 form-group">
                                        <div className="form-check">
                                            <input name="myDATAProvider" type="radio" value="PAROCHOS" {...register('myDATAProvider')} className="form-check-input" id="radio2" />
                                            <label className="form-check-label inputlabel" htmlFor="radio2">{t("parochosUsage")}</label>
                                        </div>
                                    </div>
                                    <div className="col-md-3 form-group">
                                        <label className="control-label inputlabel">{t("parochosEmail")}</label>
                                        <input name="parochosEmail" type="text" {...register('parochosEmail')} className={`form-control ${errors.parochosEmail ? 'is-invalid' : ''}`} />
                                        <div className="invalid-feedback">{errors.parochosEmail?.message}</div>
                                    </div>
                                    <div className="col-md-3 form-group">
                                        <label className="control-label inputlabel">{t('parochosSubscriptionKey')}
                                            <Tippy trigger="click" content={t("parochosSubscriptionKeyInfo")}><i className="fa fa-x2 fa-info-circle infoicon"></i></Tippy>
                                        </label>
                                        <input name="parochosSubscriptionKey" type="text" {...register('parochosSubscriptionKey')} className={`form-control ${errors.parochosSubscriptionKey ? 'is-invalid' : ''}`} />
                                        <div className="invalid-feedback">{errors.parochosSubscriptionKey?.message}</div>
                                    </div>
                                    <div className="col-md-3 form-group">
                                        <label className="control-label inputlabel">{t("parochosPassword")}</label>
                                        <div className="input-group">
                                            <input
                                                name="parochosPassword"
                                                type="text"
                                                autoComplete="new-password"
                                                {...register('parochosPassword')}
                                                ref={(e) => { register('parochosPassword').ref(e); inputRefParochosPassword.current = e; }}
                                                className={`passwordLike form-control ${errors.parochosPassword ? 'is-invalid' : ''}`}
                                            />
                                            <span className="input-group-text">
                                                <i className="fa fa-solid fa-eye" onMouseDown={() => toggleFieldVisibility(inputRefParochosPassword, true)} onMouseUp={() => toggleFieldVisibility(inputRefParochosPassword, false)} style={{ cursor: "pointer" }}></i>
                                            </span>
                                            <div className="invalid-feedback">{errors.parochosPassword?.message}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Mellon POS */}
                        <div className="accordion sale-more-accordion mb-3" id="mellonAccordion">
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="mellonHeading" style={{ position: 'relative' }}>
                                    <a className="accordion-button collapsed nonhilight sale-more-accordion-btn" href="#" data-bs-toggle="collapse" data-bs-target="#mellonCollapse" aria-expanded="false" aria-controls="mellonCollapse">
                                        <i className="fa fa-credit-card" style={{ marginRight: 6 }}></i>
                                        {t('MellonInfo')}
                                    </a>
                                    {process.env.NODE_ENV === 'development' &&
                                        <a style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 160 }} target="_blank" href="https://uat.mreceipts.com/mock_authorize/Z0FBQUFBQm1KMmpNNFlnN1FTX3hSa1didThwUHhuRGN2WlNCT1dMakNpUUZXdWpZRHM2ZnFmeDhZczNIazdra0NqSlpqS204aURaa2NCNDN6eU03bjlPenlJclByN01oSW8xcDF0R2x5c1k0SzRTbVFXNHUwWlU9/" className="btn btn-secondary btn-sm">{t("getTestCode")}</a>
                                    }
                                </h2>
                                <div id="mellonCollapse" className="accordion-collapse collapse" aria-labelledby="mellonHeading" data-bs-parent="#mellonAccordion">
                                    <div className="accordion-body" style={{ padding: '16px' }}>
                                        <div className="row g-2 align-items-end">
                                            <div className="col-md-3 form-group">
                                                <label className="control-label inputlabel">{t("mellonAuthCode")}</label>
                                                <div className="input-group">
                                                    <input name="mellonAuthCode" type="text" {...register('mellonAuthCode')} className={`form-control ${errors.mellonAuthCode ? 'is-invalid' : ''}`} />
                                                    <button onClick={reedemAuthorizationCode} className="btn btn-skin">{t("reedemAuthorizationCode")}</button>
                                                    <div className="invalid-feedback">{errors.mellonAuthCode?.message}</div>
                                                </div>
                                            </div>
                                            <div className="col-md-3 form-group">
                                                <label className="control-label inputlabel">{t("mellonApiKey")}</label>
                                                <div className="input-group">
                                                    <input
                                                        name="mellonApiKey"
                                                        type="text"
                                                        autoComplete="new-password"
                                                        {...register('mellonApiKey')}
                                                        ref={(e) => { register('mellonApiKey').ref(e); inputRefMellonApiKey.current = e; }}
                                                        className={`passwordLike form-control ${errors.mellonApiKey ? 'is-invalid' : ''}`}
                                                    />
                                                    <span className="input-group-text">
                                                        <i className="fa fa-solid fa-eye" onMouseDown={() => toggleFieldVisibility(inputRefMellonApiKey, true)} onMouseUp={() => toggleFieldVisibility(inputRefMellonApiKey, false)} style={{ cursor: "pointer" }}></i>
                                                    </span>
                                                    <div className="invalid-feedback">{errors.mellonApiKey?.message}</div>
                                                </div>
                                            </div>
                                            <div className="col-md-2 form-group">
                                                <label className="control-label inputlabel">{t("MellonTerminalType")}</label>
                                                <select name="posTerminalType" {...register('posTerminalType')} className="form-select">
                                                    {posTerminalTypes.map(type => <option key={type} value={type}>{t(type)}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-md-3 form-group">
                                                <label className="control-label inputlabel">{t("MellonTerminals")}</label>
                                                <div className="input-group">
                                                    <select name="mellonTerminals" className="form-select">
                                                        {posTerminals.map(terminal => <option key={terminal.terminalId}>{terminal.terminalId}</option>)}
                                                    </select>
                                                    <button onClick={syncTerminalList} className="btn btn-skin">{t("syncTreminalList")}</button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Extra integrations */}
                        <div className="accordion sale-more-accordion mb-3" id="companyExtraAccordion">
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="companyExtraHeading">
                                    <a className="accordion-button collapsed nonhilight sale-more-accordion-btn" href="#" data-bs-toggle="collapse" data-bs-target="#companyExtraCollapse" aria-expanded="false" aria-controls="companyExtraCollapse">
                                        <i className="fa fa-plug" style={{ marginRight: 6 }}></i>
                                        {t("extraIntegrations")}
                                    </a>
                                </h2>
                                <div id="companyExtraCollapse" className="accordion-collapse collapse" aria-labelledby="companyExtraHeading" data-bs-parent="#companyExtraAccordion">
                                    <div className="accordion-body" style={{ padding: '16px' }}>
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <div className="card extra-company-field-card h-100">
                                                    <label className="control-label inputlabel">{t('accountantEmail')}
                                                        <Tippy trigger="click" content={t("accountantEmailInfo")}><i className="fa fa-x2 fa-info-circle infoicon" style={{ marginLeft: 6 }}></i></Tippy>
                                                    </label>
                                                    <div className="card-body">
                                                        <input name="accountantEmail" {...register('accountantEmail')} type="text" className="form-control" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="card extra-company-field-card h-100">
                                                    <label className="control-label inputlabel">{t('vivaQuickPay')}
                                                        <Tippy trigger="click" content={t("vivaQuickPayInfo")}><i className="fa fa-x2 fa-info-circle infoicon" style={{ marginLeft: 6 }}></i></Tippy>
                                                    </label>
                                                    <div className="card-body">
                                                        <input name="vivaQuickPay" {...register('vivaQuickPay')} type="text" className="form-control" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="card extra-company-field-card h-100">
                                                    <label className="control-label inputlabel">{t('stripeConnect')}
                                                        <Tippy trigger="click" content={t("stripePaymentInfo")}><i className="fa fa-x2 fa-info-circle infoicon" style={{ marginLeft: 6 }}></i></Tippy>
                                                    </label>
                                                    <div className="card-body d-flex gap-2">
                                                        <input name="stripeAccountID" disabled type="text" {...register('stripeAccountID', { maxLength: 12, minLength: 11 })} className={`form-control ${errors.stripeAccountID ? 'is-invalid' : ''}`} />
                                                        <button style={{ whiteSpace: 'nowrap' }} onClick={createStripeAccount} className="btn btn-skin">{t("stripeConnect")}</button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="card extra-company-field-card h-100">
                                                    <label className="control-label inputlabel">{t('webHook')}{t("forDevs")}
                                                        <Tippy trigger="click" content={t("webHookInfo")}><i className="fa fa-x2 fa-info-circle infoicon" style={{ marginLeft: 6 }}></i></Tippy>
                                                    </label>
                                                    <div className="card-body">
                                                        <input name="webhook" {...register('webhook', { required: true, maxLength: 10 })} type="text" className="form-control" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-3">

                                                <div className="card extra-company-field-card h-100">
                                                    <label className="control-label inputlabel">{t("CheckAFM")}
                                                        <a style={{ margin: 6 }} className="btn btn-skin" href="#" onClick={() => refMyDataCheckAfmHelp.current.open()}>{t("guideForCheckAfm")}</a>


                                                    </label>
                                                    <div className="card-body d-flex flex-column gap-2">
                                                        <input name="checkAfmUsername" {...register('checkAfmUsername')} type="text" placeholder={t('checkAfmUsername')} className="form-control" />
                                                        <div className="input-group">
                                                            <input
                                                                name="checkAfmPassword"
                                                                type="text"
                                                                autoComplete="new-password"
                                                                placeholder={t('checkAfmPassword')}
                                                                {...register('checkAfmPassword')}
                                                                ref={(e) => { register('checkAfmPassword').ref(e); inputRefCheckAfmPassword.current = e; }}
                                                                className="passwordLike form-control"
                                                            />
                                                            <span className="input-group-text">
                                                                <i className="fa fa-solid fa-eye" onMouseDown={() => toggleFieldVisibility(inputRefCheckAfmPassword, true)} onMouseUp={() => toggleFieldVisibility(inputRefCheckAfmPassword, false)} style={{ cursor: "pointer" }}></i>
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <MyDataHelpModal ref={refMyDataHelp} />
                        <MyDataCheckAfmHelpModal ref={refMyDataCheckAfmHelp} ></MyDataCheckAfmHelpModal>
                        <MyDataSubscriptionModal setBlockuiLoading={setBlockuiLoading} myDATASubscriptionCallback={myDATASubscriptionCallback} ref={refMySubscriptionModal} />

                    </FormLayout>
                </form>
            </div>
        </BlockUi>
    );
}
